import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    S200ukiOnboarding as S200ukiOnboardingShard,
    S200ukiOnboardingViews as VIEWS,
    S200ukiTransitionMinMs as TRANSITION_MIN_MS,
} from '@Sage/ssm-web5';
import { ErrorActions } from 'actions';
import Logo from 'components/Logo';
import { PATH_IMAGE } from 'constants/Environment';
import { S200ukiOnboardingService } from 'services';

const {
    ADMIN_CREATE_FAILED,
    BLOCKER_CODES,
    SYNC_ERROR_CODES,
    bootstrap,
    getCompanies,
    redirectToSageIdLogin,
    resetOnboardingSession,
    runSync,
    refreshSessionCache,
} = S200ukiOnboardingService;
import { checkDeviceAccess } from 'utils/fm';

import './styles.scss';

const BG_IMAGE_SRC = `${PATH_IMAGE}onboarding-background.jpg`;

const stripTransientQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    let changed = false;

    ['code', 'state', 'sessionId'].forEach((key) => {
        if (params.has(key)) {
            params.delete(key);
            changed = true;
        }
    });

    if (!changed) return;

    const query = params.toString();
    const uri = query
        ? `${window.location.pathname}?${query}${window.location.hash}`
        : `${window.location.pathname}${window.location.hash}`;

    window.history.replaceState({}, document.title, uri);
};

const readSessionIdFromUrl = (sessionIdRef) => {
    const fromUrl = new URLSearchParams(window.location.search).get('sessionId');
    if (fromUrl) {
        sessionIdRef.current = fromUrl;
    }
    return sessionIdRef.current;
};

const getBootstrapParams = (sessionIdRef) => ({
    sessionId: readSessionIdFromUrl(sessionIdRef),
});

const S200ukiOnboarding = memo(() => {
    const dispatch = useDispatch();

    // --- State
    const [view, setView] = useState(VIEWS.BOOTSTRAP);
    const [companies, setCompanies] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [maxSelectable, setMaxSelectable] = useState(5);
    const [blockerCode, setBlockerCode] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [postLoginTargetView, setPostLoginTargetView] = useState(null);
    const [canDeviceAccess, setCanDeviceAccess] = useState(() => checkDeviceAccess());
    const [syncActiveStep, setSyncActiveStep] = useState(2);
    const [syncIsSuccess, setSyncIsSuccess] = useState(false);
    const [syncErrorCode, setSyncErrorCode] = useState(null);
    const [syncFlowStatus, setSyncFlowStatus] = useState('idle');
    const transitionStartedAtRef = useRef(null);
    const sessionIdRef = useRef(null);
    const syncStartedRef = useRef(false);
    const syncRedirectedRef = useRef(false);
    const redirectUrlRef = useRef(null);

    // --- Handlers
    const showError = useCallback(() => {
        dispatch(ErrorActions.showError());
    }, [dispatch]);

    const resetSyncFlow = useCallback(() => {
        syncStartedRef.current = false;
        syncRedirectedRef.current = false;
        redirectUrlRef.current = null;
        setSyncActiveStep(2);
        setSyncIsSuccess(false);
        setSyncErrorCode(null);
        setSyncFlowStatus('idle');
    }, []);

    const resolveCompaniesView = useCallback((result, list) => {
        if (result?.needsSageIdLogin) {
            return VIEWS.SAGEID_LOGIN;
        }
        if (!result?.success) {
            return VIEWS.COMPANIES_ERROR;
        }
        if (list.length === 0) {
            return VIEWS.NO_COMPANIES;
        }
        return VIEWS.COMPANIES;
    }, []);

    const resolveBlockerFromResult = useCallback((result, { advanceView = true } = {}) => {
        const code = result?.errorCode;
        if (!code || !Object.values(BLOCKER_CODES).includes(code)) {
            return false;
        }
        setBlockerCode(code);
        if (advanceView) {
            setView(VIEWS.BLOCKER);
        } else {
            setPostLoginTargetView(VIEWS.BLOCKER);
        }
        return true;
    }, []);

    const loadCompanies = useCallback(
        ({ advanceView = true } = {}) => {
            setIsSubmitting(true);
            const sessionId = readSessionIdFromUrl(sessionIdRef);

            return getCompanies({ sessionId })
                .then((result) => {
                    if (resolveBlockerFromResult(result, { advanceView })) {
                        return;
                    }

                    const list = result?.success ? result.companies || [] : [];
                    if (result?.success) {
                        setCompanies(list);
                    }

                    const targetView = resolveCompaniesView(result, list);

                    if (advanceView) {
                        setView(targetView);
                        return;
                    }

                    setPostLoginTargetView(targetView);
                })
                .catch((e) => {
                    console.error(e);
                    if (advanceView) {
                        setView(VIEWS.COMPANIES_ERROR);
                        return;
                    }
                    setPostLoginTargetView(VIEWS.COMPANIES_ERROR);
                })
                .finally(() => setIsSubmitting(false));
        },
        [resolveBlockerFromResult, resolveCompaniesView],
    );

    const runBootstrap = useCallback(() => {
        setView(VIEWS.BOOTSTRAP);
        setBlockerCode(null);
        resetSyncFlow();
        setIsSubmitting(true);

        return bootstrap(getBootstrapParams(sessionIdRef))
            .then((result) => {
                if (result?.needsSageIdLogin) {
                    setView(VIEWS.SAGEID_LOGIN);
                    return;
                }
                if (result?.token) {
                    // Phase 2: axios.setToken(result.token);
                }

                if (!result?.success) {
                    if (result?.errorCode === ADMIN_CREATE_FAILED) {
                        setView(VIEWS.ADMIN_CREATE_FAILED);
                        return;
                    }
                    if (result?.errorCode) {
                        setBlockerCode(result.errorCode);
                        setView(VIEWS.BLOCKER);
                        return;
                    }
                    showError();
                    return;
                }

                setMaxSelectable(result.maxSelectable ?? 5);
                setPostLoginTargetView(null);
                transitionStartedAtRef.current = Date.now();
                setView(VIEWS.SAGEID_SUCCESS);
                loadCompanies({ advanceView: false });
            })
            .catch((e) => {
                console.error(e);
                showError();
            })
            .finally(() => {
                setIsSubmitting(false);
                stripTransientQueryParams();
            });
    }, [loadCompanies, resetSyncFlow, showError]);

    const handleToggleCompany = useCallback(
        (id) => {
            setSelectedIds((prev) => {
                if (prev.includes(id)) {
                    return prev.filter((x) => x !== id);
                }
                const effectiveMax =
                    companies.length > 0
                        ? Math.min(maxSelectable, companies.length)
                        : maxSelectable;
                if (prev.length >= effectiveMax) {
                    return prev;
                }
                return [...prev, id];
            });
        },
        [companies.length, maxSelectable],
    );

    const handleConfirmSync = useCallback(() => {
        resetSyncFlow();
        setConfirmModalOpen(false);
        setSyncFlowStatus('running');
        setSyncActiveStep(2);
        setView(VIEWS.SYNC_PROGRESS);
    }, [resetSyncFlow]);

    const handleRetrySync = useCallback(() => {
        resetSyncFlow();
        setSyncFlowStatus('running');
        setSyncActiveStep(2);
        setView(VIEWS.SYNC_PROGRESS);
    }, [resetSyncFlow]);

    const handleOpenConfirm = useCallback(() => setConfirmModalOpen(true), []);
    const handleCancelConfirm = useCallback(() => setConfirmModalOpen(false), []);

    const handleRetryCompanies = useCallback(() => {
        setIsSubmitting(true);
        const sessionId = readSessionIdFromUrl(sessionIdRef);

        const load = refreshSessionCache(sessionId).then((result) => {
            if (result?.needsSageIdLogin) {
                setView(VIEWS.SAGEID_LOGIN);
                return null;
            }
            if (resolveBlockerFromResult(result)) {
                return null;
            }
            if (!result?.success) {
                setView(VIEWS.COMPANIES_ERROR);
                return null;
            }
            return loadCompanies();
        });

        load.catch((e) => {
            console.error(e);
            setView(VIEWS.COMPANIES_ERROR);
        }).finally(() => setIsSubmitting(false));
    }, [loadCompanies, resolveBlockerFromResult]);

    const handleConnectSageId = useCallback(() => {
        redirectToSageIdLogin();
    }, []);

    const handleSyncProgressComplete = useCallback(() => {
        if (syncRedirectedRef.current) {
            return;
        }

        const url = redirectUrlRef.current;
        if (!url) {
            setSyncErrorCode(SYNC_ERROR_CODES.CREATE_FAILED);
            setView(VIEWS.SYNC_FAILED);
            return;
        }

        syncRedirectedRef.current = true;
        window.location.href = url;
    }, []);

    // --- Effects
    useEffect(() => {
        if (
            view !== VIEWS.SYNC_PROGRESS ||
            syncFlowStatus !== 'running' ||
            syncStartedRef.current
        ) {
            return undefined;
        }

        syncStartedRef.current = true;
        const sessionId = readSessionIdFromUrl(sessionIdRef);

        runSync(selectedIds, {
            sessionId,
            onPhase: (phase) => {
                if (phase === 'creating') {
                    setSyncActiveStep(2);
                } else if (phase === 'polling') {
                    setSyncActiveStep(3);
                } else if (phase === 'complete') {
                    setSyncActiveStep(4);
                    setSyncIsSuccess(true);
                    setSyncFlowStatus('success');
                }
            },
        })
            .then((result) => {
                if (result?.needsSageIdLogin) {
                    resetSyncFlow();
                    setView(VIEWS.SAGEID_LOGIN);
                    return;
                }

                if (!result?.success) {
                    resetSyncFlow();
                    const errorCode = result?.errorCode ?? SYNC_ERROR_CODES.CREATE_FAILED;
                    if (
                        errorCode === SYNC_ERROR_CODES.CREATE_TIMEOUT ||
                        errorCode === SYNC_ERROR_CODES.CREATE_FAILED
                    ) {
                        setSyncErrorCode(errorCode);
                        setView(VIEWS.SYNC_FAILED);
                        return;
                    }
                    showError();
                    setView(VIEWS.COMPANIES);
                    return;
                }

                if (result.redirectUrl) {
                    redirectUrlRef.current = result.redirectUrl;
                }
            })
            .catch((e) => {
                console.error(e);
                resetSyncFlow();
                showError();
                setView(VIEWS.COMPANIES);
            });

        return undefined;
    }, [resetSyncFlow, selectedIds, showError, syncFlowStatus, view]);

    useEffect(() => {
        if (view !== VIEWS.SAGEID_SUCCESS || !postLoginTargetView) {
            return undefined;
        }

        const elapsed = Date.now() - (transitionStartedAtRef.current ?? Date.now());
        const remaining = Math.max(0, TRANSITION_MIN_MS - elapsed);

        const timer = setTimeout(() => {
            setView(postLoginTargetView);
            setPostLoginTargetView(null);
        }, remaining);

        return () => clearTimeout(timer);
    }, [view, postLoginTargetView]);

    useEffect(() => {
        const handleResize = () => {
            setCanDeviceAccess(checkDeviceAccess());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        resetOnboardingSession();

        if (!canDeviceAccess) {
            setView(VIEWS.MOBILE_BLOCKER);
            return;
        }

        runBootstrap();
    }, [canDeviceAccess, runBootstrap]);

    // --- Memoized props
    const logo = useMemo(() => <Logo />, []);

    const shardProps = useMemo(
        () => ({
            bgImgSrc: BG_IMAGE_SRC,
            blockerCode,
            companies,
            confirmModalOpen,
            isSubmitting,
            logo,
            maxSelectable,
            onCancelConfirm: handleCancelConfirm,
            onConfirmSync: handleConfirmSync,
            onConnectSageId: handleConnectSageId,
            onOpenConfirm: handleOpenConfirm,
            onRetryBootstrap: runBootstrap,
            onRetryCompanies: handleRetryCompanies,
            onRetrySync: handleRetrySync,
            onSyncProgressComplete: handleSyncProgressComplete,
            onToggleCompany: handleToggleCompany,
            selectedIds,
            syncActiveStep,
            syncErrorCode,
            syncIsSuccess,
            view,
        }),
        [
            blockerCode,
            companies,
            confirmModalOpen,
            handleCancelConfirm,
            handleConfirmSync,
            handleConnectSageId,
            handleOpenConfirm,
            handleRetryCompanies,
            handleRetrySync,
            handleSyncProgressComplete,
            handleToggleCompany,
            isSubmitting,
            logo,
            maxSelectable,
            runBootstrap,
            selectedIds,
            syncActiveStep,
            syncErrorCode,
            syncIsSuccess,
            view,
        ],
    );

    // --- Render
    return (
        <div className="fm-s200uki-onboarding">
            <S200ukiOnboardingShard {...shardProps} />
        </div>
    );
});

S200ukiOnboarding.displayName = 'S200ukiOnboarding';

export default S200ukiOnboarding;
